/**
 * sessionStorage mirror for the per-contentId `previousEmojiId` cache used by
 * the Live Docs "Restore emoji" affordance. Survives same-tab refresh; not a
 * source of truth — the in-memory unstated state is. Failures degrade silently.
 */

import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { sessionKeys } from '@confluence/storage-manager/entry-points/SessionStorageKeys.ts';

const STORAGE_KEY = sessionKeys.EMOJI_TITLE_PREVIOUS_EMOJI;

// Versioned so the format can evolve without crashing on stale entries.
type StoredShape = {
	v: 1;
	entries: Record<string, string>;
};

export const readSessionStoragePreviousEmojiCache = (): Record<string, string> => {
	try {
		const raw = sessionStorage?.getItem(STORAGE_KEY);
		if (!raw || typeof raw !== 'string') {
			return {};
		}

		const parsed = JSON.parse(raw) as Partial<StoredShape>;
		if (!parsed || parsed.v !== 1 || typeof parsed.entries !== 'object' || !parsed.entries) {
			return {};
		}

		// Defensive: filter to non-empty string values in case the slot was
		// mutated by something else on the same origin.
		const out: Record<string, string> = {};
		for (const [contentId, emojiId] of Object.entries(parsed.entries)) {
			if (typeof emojiId === 'string' && emojiId.length > 0) {
				out[contentId] = emojiId;
			}
		}
		return out;
	} catch {
		return {};
	}
};

export const writeSessionStoragePreviousEmojiCache = (map: Record<string, string | null>): void => {
	try {
		const entries: Record<string, string> = {};
		for (const [contentId, emojiId] of Object.entries(map)) {
			if (typeof emojiId === 'string' && emojiId.length > 0) {
				entries[contentId] = emojiId;
			}
		}

		// Drop the key entirely when empty to keep storage clean.
		if (Object.keys(entries).length === 0) {
			sessionStorage?.removeItem(STORAGE_KEY);
			return;
		}

		sessionStorage?.setItem(STORAGE_KEY, JSON.stringify({ v: 1, entries } satisfies StoredShape));
	} catch {
		// Quota / consent / disabled storage — swallow; in-memory cache is unaffected.
	}
};

// Exposed for tests only.
export const SESSION_STORAGE_KEY_FOR_TESTS: string = STORAGE_KEY;
